import React from 'react';
import {
    FaFolder, FaFilePdf, FaFileImage, FaFileWord, FaFileExcel, FaFileVideo, FaFile,
} from 'react-icons/fa';

export const GOOGLE_APPS_MIME = 'application/vnd.google-apps.';

export function formatFileSize(bytes) {
    if (!bytes) return '—';
    const b = Number(bytes);
    if (b < 1024) return `${b} B`;
    if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1048576).toFixed(1)} MB`;
}

// Date-only values (YYYY-MM-DD) are parsed as local dates so they never shift a day.
export function formatDate(value) {
    if (!value) return '—';
    const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? new Date(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, Number(value.slice(8, 10)))
        : new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function isPreviewable(mimeType) {
    return mimeType === 'application/pdf' || Boolean(mimeType?.startsWith('image/'));
}

export function isGoogleAppsFile(mimeType) {
    return Boolean(mimeType?.startsWith(GOOGLE_APPS_MIME));
}

export function FileIcon({ mimeType }) {
    if (!mimeType) return <FaFile className="text-gray-400" />;
    if (mimeType === 'application/vnd.google-apps.folder') return <FaFolder className="text-yellow-500" />;
    if (mimeType === 'application/pdf') return <FaFilePdf className="text-red-500" />;
    if (mimeType.startsWith('image/')) return <FaFileImage className="text-blue-500" />;
    if (mimeType.startsWith('video/')) return <FaFileVideo className="text-purple-500" />;
    if (mimeType.includes('wordprocessingml') || mimeType === 'application/msword' || mimeType === 'application/vnd.google-apps.document')
        return <FaFileWord className="text-blue-700" />;
    if (mimeType.includes('spreadsheetml') || mimeType === 'application/vnd.ms-excel' || mimeType === 'application/vnd.google-apps.spreadsheet')
        return <FaFileExcel className="text-green-600" />;
    return <FaFile className="text-gray-400" />;
}

export const STATUS_STYLES = {
    valid: { label: 'Valid', className: 'bg-green-50 text-green-700 border-green-200' },
    expiring: { label: 'Expiring soon', className: 'bg-amber-50 text-amber-700 border-amber-200' },
    expired: { label: 'Expired', className: 'bg-red-50 text-red-700 border-red-200' },
    missing: { label: 'Missing', className: 'bg-red-50 text-red-700 border-red-200' },
    no_expiry: { label: 'No expiry set', className: 'bg-amber-50 text-amber-700 border-amber-200' },
    optional: { label: 'Not uploaded', className: 'bg-gray-50 text-gray-500 border-gray-200' },
};

export function StatusBadge({ status }) {
    const style = STATUS_STYLES[status];
    if (!style) return null;
    return (
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${style.className}`}>
            {style.label}
        </span>
    );
}

export function apiErrorMessage(err, fallback) {
    const data = err?.response?.data;
    if (data instanceof Blob) return fallback;
    return data?.message || data?.error || err?.message || fallback;
}

// Blob requests return their JSON error bodies as Blobs; unwrap them for a readable message.
export async function blobApiErrorMessage(err, fallback) {
    const data = err?.response?.data;
    if (data instanceof Blob) {
        try {
            const text = typeof data.text === 'function'
                ? await data.text()
                : await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsText(data);
                });
            const parsed = JSON.parse(text);
            return parsed.message || fallback;
        } catch {
            return fallback;
        }
    }
    return apiErrorMessage(err, fallback);
}

export const selectPortalProps = {
    menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
    styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
};
